import React, { useState, useContext, useEffect, useMemo } from 'react';

// GLOBAL VARIABLES
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';
import { SALES_FILE_TYPE_IDS } from 'globals.js';

// DEPENDENCIES
import { useFormikContext } from 'formik';

// GLOBAL FUNCTIONS
import { controlName } from 'functions';

// GLOBAL HELPERS/CONTEXT
import { RecordContext } from 'helpers/getRecordData';
import useRecordView from 'helpers/getRecordView';

// CORE COMPONENTS
import Form from 'core/form/Form';

// PARTS
import Card from 'parts/card/Card';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';

// VARIABLES
const DOCUMENT_TYPES = {
  0: {
    1: {
      edp: SALES_FILE_TYPE_IDS.EDP_RETAIL_USED,
      ebos: SALES_FILE_TYPE_IDS.EBOS_RETAIL_USED,
      rebate: SALES_FILE_TYPE_IDS.REBATE_RETAIL_USED,
      acct_docs: SALES_FILE_TYPE_IDS.ACCOUNTING_TAG_TITLE_DOCS_RETAIL_USED,
      other: SALES_FILE_TYPE_IDS.OTHER_RETAIL_USED,
      amfi_product: SALES_FILE_TYPE_IDS.AMFI_PRODUCT_RETAIL_USED
    },
    2: {
      edp: SALES_FILE_TYPE_IDS.EDP_WHOLESALE_USED,
      ebos: SALES_FILE_TYPE_IDS.EBOS_WHOLESALE_USED,
      rebate: SALES_FILE_TYPE_IDS.REBATE_WHOLESALE_USED,
      acct_docs: SALES_FILE_TYPE_IDS.ACCOUNTING_TAG_TITLE_DOCS_WHOLESALE_USED,
      other: SALES_FILE_TYPE_IDS.OTHER_WHOLESALE_USED,
      amfi_product: SALES_FILE_TYPE_IDS.AMFI_PRODUCT_RETAIL_USED // Wholesale Detailed
    }
  },
  1: {
    1: {
      edp: SALES_FILE_TYPE_IDS.EDP_RETAIL_NEW,
      ebos: SALES_FILE_TYPE_IDS.EBOS_RETAIL_NEW,
      rebate: SALES_FILE_TYPE_IDS.REBATE_RETAIL_NEW,
      acct_docs: SALES_FILE_TYPE_IDS.ACCOUNTING_TAG_TITLE_DOCS_RETAIL_NEW,
      other: SALES_FILE_TYPE_IDS.OTHER_RETAIL_NEW,
      amfi_product: SALES_FILE_TYPE_IDS.AMFI_PRODUCT_RETAIL_NEW
    },
    2: {
      edp: SALES_FILE_TYPE_IDS.EDP_WHOLESALE_NEW,
      ebos: SALES_FILE_TYPE_IDS.EBOS_WHOLESALE_NEW,
      rebate: SALES_FILE_TYPE_IDS.REBATE_WHOLESALE_NEW,
      acct_docs: SALES_FILE_TYPE_IDS.ACCOUNTING_TAG_TITLE_DOCS_WHOLESALE_NEW,
      other: SALES_FILE_TYPE_IDS.OTHER_WHOLESALE_NEW,
      amfi_product: SALES_FILE_TYPE_IDS.AMFI_PRODUCT_RETAIL_NEW // Wholesale Detailed
    }
  }
};

// FORM COMPONENT
const SaleAttachmentsForm = () => {
  // CONTEXT
  const { record: { aryRebate = [], inventory = {}, ...record } = {} } = useContext(RecordContext);
  const { values, setFieldValue } = useFormikContext() || {};
  const { isAdd } = useRecordView();

  // STATE
  const [ isDelivered, setIsDelivered ] = useState(false);

  // MEMOS
  const {
    aryDealerAMFinance = [],
    documentAttachment = {},
    process_through_finance,
    sale_status_log,
    sale_type_category_id,
    wholesale_format_id
  } = useMemo(
    () => values || {},
    [values]
  )
  const { new_used } = useMemo(() => (isAdd ? record : inventory), [record, inventory, isAdd]);
  
  const isRetail = useMemo(
    () => parseInt(sale_type_category_id) === RETAIL_WHOLESALE.retail.numeric,
    [sale_type_category_id]
  );

  const isWholesaleSimplified = useMemo(() => {
    return (
      parseInt(sale_type_category_id) === RETAIL_WHOLESALE.wholesale.numeric &&
      parseInt(wholesale_format_id) === WHOLESALE_FORMAT.simplified.numeric
    );
  }, [sale_type_category_id, wholesale_format_id]);

  const documentTypes = useMemo(
    () => DOCUMENT_TYPES[parseInt(new_used)]?.[parseInt(sale_type_category_id)] || {},
    [sale_type_category_id, new_used]
  );

  const processThroughFinance = useMemo(
    () => !isWholesaleSimplified || (isWholesaleSimplified && process_through_finance),
    [isWholesaleSimplified, process_through_finance]
  );

  const edpLabel = useMemo(
    () => `${isRetail ? 'Executed Deal Package' : "Auction Invoice / WS Buyer's Order"} (SA2)`,
    [isRetail]
  );
  
  // EFFECTS
  useEffect(
    () => {
      if (sale_status_log) {
        const delivered = sale_status_log.some(status => status.status_name === 'Delivered');
        setIsDelivered(delivered);
      }
    }, 
    [sale_status_log, setIsDelivered]
  )

  return (
    <Form.Body>
      {!isAdd || isWholesaleSimplified ? (
        <Attachment.Upload
          label={edpLabel}
          name="documentAttachment.edp"
          documentType={documentTypes.edp}
          documentCode="edp"
          downloadRecent
          requestedFor="Sales"
        />
      ) : null}
      {!isAdd ? (
        <>
          <Attachment.Upload
            label="Accounting Invoice / Bill of Sale (SA3)"
            name={controlName('ebos', 'documentAttachment')}
            documentType={documentTypes.ebos}
            documentCode='ebos'
            downloadRecent
          />
          {aryRebate.map((v, i) => !v.require_customer_signature_upload ? null : (
            <Attachment.Upload
              key={`${v.sales_rebate_id}-${i}`}
              label={`Customer Sig Docs (${v.item_name})`}
              name={controlName('aryCustSigRebateAttachment', 'aryRebate', i)}
              documentType={documentTypes.rebate}
              documentCode='rebate'
              uploadParams={{
                salesRebateId: v.sales_rebate_id
              }}
              requestParams={{
                sales_rebate_id: v.sales_rebate_id
              }}
              onUpload={(_uploadID, _file, _meta, fieldValue) => {
                // Need to add to documentAttachment
                const rebateAttachments = [...(documentAttachment?.rebate || []), fieldValue];
                setFieldValue(`documentAttachment.rebate`, rebateAttachments)
              }}
              onDelete={(_uploadID, _file, _meta, { temp_attachment_id, salesattachment_id }) => {
                // Need to remove from documentAttachment
                const rebateAttachments = (documentAttachment?.rebate || []).filter(r =>
                  temp_attachment_id
                    ? r.temp_attachment_id !== temp_attachment_id
                    : r.salesattachment_id !== salesattachment_id
                );
                setFieldValue(`documentAttachment.rebate`, rebateAttachments)
              }}
            />
          ))}
          {processThroughFinance &&
            aryDealerAMFinance.map((v, i) => parseInt(v.product_type) === 0 && v.is_checked ? (
              <Attachment.Upload
                key={v.dealer_amfinance_id}
                label={`${v.item_name} - Executed Agreement`}
                name={controlName('aryAMFinanceAttachment', 'aryDealerAMFinance', i)}
                documentType={documentTypes.amfi_product}
                documentCode='amfi_product'
                uploadParams={{
                  dealerAMFinanceId: v.dealer_amfinance_id
                }}
                multiple={false}
                onUpload={(_uploadID, _file, _meta, fieldValue) => {
                  // Need to add to documentAttachment
                  const value = { dealer_amfinance_id: v.dealer_amfinance_id, ...fieldValue };
                  const amfiProductAttachments = [...(documentAttachment?.amfi_product || []), value];
                  setFieldValue(`documentAttachment.amfi_product`, amfiProductAttachments)
                }}
                onDelete={(_uploadID, _file, _meta, { temp_attachment_id, salesattachment_id }) => {
                  // Need to remove from documentAttachment
                  const amfiProductAttachments = (documentAttachment?.amfi_product || []).filter(r =>
                    temp_attachment_id
                      ? r.temp_attachment_id !== temp_attachment_id
                      : r.salesattachment_id !== salesattachment_id
                  );
                  setFieldValue(`documentAttachment.amfi_product`, amfiProductAttachments)
                }}
              />
            ) : null
          )}
          {isDelivered && (
            <Attachment.Upload
              label="Accounting Tag & Title Docs"
              name={controlName('acct_docs', 'documentAttachment')}
              documentType={documentTypes.acct_docs}
              documentCode='acct_docs'
            />
          )}
        </>
      ) : null}
      <Attachment.Upload 
        label="Other" 
        name={controlName('other', 'documentAttachment')}
        documentType={documentTypes.other}
        documentCode='other'
        requestedFor="Sales"
      />
    </Form.Body>
  )
}

export default function SaleAttachments(props) {
  return <Card title="Sale Attachments" body={<SaleAttachmentsForm {...props} />} />;
}
